import { uiText, useUiLanguage } from '@/i18n/interface';
import React from 'react';
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

interface BeltLevel {
  id: string;
  name: string;
  color: string;
  order_sequence: number;
}

interface StudentData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  postal_code: string;
  date_of_birth: string;
  belt_level_id: string;
  join_date: string;
}

interface StudentFormFieldsProps {
  studentData: StudentData;
  setStudentData: React.Dispatch<React.SetStateAction<StudentData>>;
  beltLevels: BeltLevel[];
  selectedStudentBelt?: BeltLevel | null;
}

const StudentFormFields: React.FC<StudentFormFieldsProps> = ({
  studentData,
  setStudentData,
  beltLevels,
  selectedStudentBelt
}) => {
  useUiLanguage();
  const getBeltColorClass = (color: string) => {
    const colorMap: { [key: string]: string } = {
      white: 'bg-gray-100 text-gray-900 border border-gray-300',
      orange: 'bg-orange-500 text-white',
      blue: 'bg-blue-500 text-white',
      yellow: 'bg-yellow-400 text-black',
      green: 'bg-green-500 text-white',
      purple: 'bg-purple-500 text-white',
      brown: 'bg-amber-700 text-white',
      black: 'bg-black text-white'
    };
    return colorMap[color] || 'bg-gray-500 text-white';
  };

  const getBeltColorForDropdown = (color: string) => {
    const colorMap: { [key: string]: string } = {
      white: 'bg-gray-100',
      orange: 'bg-orange-500',
      blue: 'bg-blue-500',
      yellow: 'bg-yellow-400',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      brown: 'bg-amber-700',
      black: 'bg-black'
    };
    return colorMap[color] || 'bg-gray-500';
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="studentFirstName">{uiText("First Name")}</Label>
        <Input
          id="studentFirstName"
          value={studentData.first_name}
          onChange={(e) => setStudentData(prev => ({ ...prev, first_name: e.target.value }))}
        />
      </div>
      <div>
        <Label htmlFor="studentLastName">{uiText("Last Name")}</Label>
        <Input
          id="studentLastName"
          value={studentData.last_name}
          onChange={(e) => setStudentData(prev => ({ ...prev, last_name: e.target.value }))}
        />
      </div>
      <div>
        <Label htmlFor="studentEmail">{uiText("Email")}</Label>
        <Input
          id="studentEmail"
          type="email"
          value={studentData.email}
          onChange={(e) => setStudentData(prev => ({ ...prev, email: e.target.value }))}
        />
      </div>
      <div>
        <Label htmlFor="studentPhone">{uiText("Phone")}</Label>
        <Input
          id="studentPhone"
          value={studentData.phone}
          onChange={(e) => setStudentData(prev => ({ ...prev, phone: e.target.value }))}
        />
      </div>
      <div>
        <Label htmlFor="studentAddress">{uiText("Address")}</Label>
        <Input
          id="studentAddress"
          value={studentData.address}
          onChange={(e) => setStudentData(prev => ({ ...prev, address: e.target.value }))}
        />
      </div>
      <div>
        <Label htmlFor="studentBeltLevel">{uiText("Belt Level")}</Label>
        {selectedStudentBelt && (
          <div className="mb-2">
            <Badge className={`${getBeltColorClass(selectedStudentBelt.color)} text-xs`}>
              {uiText("Current:")}{' '}{selectedStudentBelt.name}
            </Badge>
          </div>
        )}
        <Select
          value={studentData.belt_level_id || "none"}
          onValueChange={(value) => setStudentData(prev => ({ ...prev, belt_level_id: value === "none" ? "" : value }))}
        >
          <SelectTrigger>
            <SelectValue placeholder={uiText("Select belt level")} />
          </SelectTrigger>
          <SelectContent className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg z-50">
            <SelectItem value="none">{uiText("No Belt Assigned")}</SelectItem>
            {beltLevels.map((belt) => (
              <SelectItem key={belt.id} value={belt.id}>
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${getBeltColorForDropdown(belt.color)} border border-gray-300`} />
                  <span>{belt.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="studentCity">{uiText("City")}</Label>
        <Input
          id="studentCity"
          value={studentData.city}
          onChange={(e) => setStudentData(prev => ({ ...prev, city: e.target.value }))}
        />
      </div>
      <div>
        <Label htmlFor="studentPostalCode">{uiText("Postal Code")}</Label>
        <Input
          id="studentPostalCode"
          value={studentData.postal_code}
          onChange={(e) => setStudentData(prev => ({ ...prev, postal_code: e.target.value }))}
        />
      </div>
      <div>
        <Label htmlFor="studentDateOfBirth">{uiText("Date of Birth")}</Label>
        <Input
          id="studentDateOfBirth"
          type="date"
          value={studentData.date_of_birth}
          onChange={(e) => setStudentData(prev => ({ ...prev, date_of_birth: e.target.value }))}
        />
      </div>
      <div>
        <Label htmlFor="studentJoinDate">{uiText("Join Date")}</Label>
        <Input
          id="studentJoinDate"
          type="date"
          value={studentData.join_date}
          onChange={(e) => setStudentData(prev => ({ ...prev, join_date: e.target.value }))}
        />
      </div>
    </div>
  );
};

export default StudentFormFields;